/**
 * ExecutionSignalCard
 * 
 * Displays a single execution signal with confirmation actions.
 * 
 * UX Principle #4: Execution ≠ Choice
 * Allowed Actions:
 * - Confirm Complete
 * - Block (with reason)
 * - Request Clarification
 * - Escalate Constraint
 * 
 * UX Principle #5: Confirmation Over Explanation
 * - Binary confirmation actions
 * - No narrative text inputs
 * - Structured responses only
 * 
 * UX Principle #7: Exceptions Are First-Class
 * - Signaling an exception must be as easy as confirming completion
 */

import React, { useState } from 'react';
import { ExecutionSignal, BlockReason, BLOCK_REASON_LABELS } from '../../types/executionSignal';
import { BlockReasonSelector } from './BlockReasonSelector';
import {
  CheckCircleOutlined,
  StopOutlined,
  QuestionCircleOutlined,
  ArrowUpOutlined,
  DownOutlined,
  UpOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { Spin, Input } from 'antd';

const { TextArea } = Input;

export interface ExecutionSignalHandlers {
  onComplete: (signalId: string, notes?: string) => Promise<void>;
  onBlock: (signalId: string, reason: BlockReason, notes?: string) => Promise<void>;
  onEscalate: (signalId: string, reason: string, toRole?: string) => Promise<void>;
  onClarify: (signalId: string, question: string) => Promise<void>;
}

interface ExecutionSignalCardProps extends ExecutionSignalHandlers {
  signal: ExecutionSignal;
  isResolving?: boolean;
  compact?: boolean;
}

type ActionMode = 'none' | 'block' | 'escalate' | 'clarify';

export const ExecutionSignalCard: React.FC<ExecutionSignalCardProps> = ({
  signal,
  onComplete,
  onBlock,
  onEscalate,
  onClarify,
  isResolving = false,
  compact = false
}) => {
  const [actionMode, setActionMode] = useState<ActionMode>('none');
  const [blockReason, setBlockReason] = useState<BlockReason | null>(null);
  const [blockNotes, setBlockNotes] = useState('');
  const [escalationReason, setEscalationReason] = useState('');
  const [clarificationQuestion, setClarificationQuestion] = useState('');
  const [showContext, setShowContext] = useState(false);
  const [localResolving, setLocalResolving] = useState(false);

  const isProcessing = isResolving || localResolving;

  // Get priority color
  const getPriorityColor = (priority: number) => {
    if (priority <= 2) return { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' };
    if (priority <= 4) return { bg: '#fffbeb', text: '#d97706', border: '#fcd34d' };
    return { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' };
  };

  const priorityColors = getPriorityColor(signal.priority);

  // Get signal type icon
  const getSignalTypeIcon = () => {
    switch (signal.signal_type) {
      case 'transition_required':
        return <ArrowUpOutlined style={{ color: '#3b82f6' }} />;
      case 'approval_needed':
        return <CheckCircleOutlined style={{ color: '#10b981' }} />;
      case 'capacity_exceeded':
        return <ExclamationCircleOutlined style={{ color: '#f59e0b' }} />;
      default:
        return <ClockCircleOutlined style={{ color: '#6b7280' }} />;
    }
  };

  // Handle complete action
  const handleComplete = async () => {
    setLocalResolving(true);
    try {
      await onComplete(signal.id);
    } finally {
      setLocalResolving(false);
    }
  };

  // Handle block submission
  const handleBlockSubmit = async () => {
    if (!blockReason) return;
    setLocalResolving(true);
    try {
      await onBlock(signal.id, blockReason, blockNotes || undefined);
      setActionMode('none');
      setBlockReason(null);
      setBlockNotes('');
    } finally {
      setLocalResolving(false);
    }
  };

  // Handle escalate submission
  const handleEscalateSubmit = async () => {
    if (!escalationReason.trim()) return;
    setLocalResolving(true);
    try {
      await onEscalate(signal.id, escalationReason);
      setActionMode('none');
      setEscalationReason('');
    } finally {
      setLocalResolving(false);
    }
  };

  // Handle clarify submission
  const handleClarifySubmit = async () => {
    if (!clarificationQuestion.trim()) return;
    setLocalResolving(true);
    try {
      await onClarify(signal.id, clarificationQuestion);
      setActionMode('none');
      setClarificationQuestion('');
    } finally {
      setLocalResolving(false);
    }
  };

  // Render context section (pull-based per UX Principle #6)
  const renderContext = () => {
    const ctx = signal.context;
    if (!ctx || Object.keys(ctx).length === 0) return null;

    const contextItems = [
      ctx.artifact_type && { label: 'Type', value: ctx.artifact_type },
      ctx.artifact_name && { label: 'Name', value: ctx.artifact_name },
      ctx.current_state && { label: 'Current State', value: ctx.current_state },
      ctx.target_state && { label: 'Target State', value: ctx.target_state },
      ctx.waiting_on?.length && { label: 'Waiting On', value: ctx.waiting_on.join(', ') }
    ].filter(Boolean);

    if (contextItems.length === 0) return null;

    return (
      <div style={{ marginTop: '12px' }}>
        <button
          onClick={() => setShowContext(!showContext)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 0',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#6b7280',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          {showContext ? <UpOutlined /> : <DownOutlined />}
          {showContext ? 'Hide context' : 'Show context'}
        </button>
        
        {showContext && (
          <div style={{
            marginTop: '8px',
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '6px',
            fontSize: '13px'
          }}>
            {contextItems.map((item, index) => item && (
              <div
                key={index}
                style={{
                  display: 'flex',
                  marginBottom: index < contextItems.length - 1 ? '6px' : 0
                }}
              >
                <span style={{ color: '#6b7280', minWidth: '100px' }}>{item.label}:</span>
                <span style={{ color: '#111827', fontWeight: 500 }}>{item.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render action buttons
  const renderActions = () => {
    if (actionMode !== 'none') return null;

    const buttonStyle = (primary: boolean) => ({
      flex: 1,
      padding: compact ? '10px 12px' : '12px 16px',
      fontSize: compact ? '13px' : '14px',
      fontWeight: 600,
      borderRadius: '8px',
      border: primary ? 'none' : '1px solid #e5e7eb',
      backgroundColor: primary ? '#10b981' : 'white',
      color: primary ? 'white' : '#374151',
      cursor: isProcessing ? 'not-allowed' : 'pointer',
      opacity: isProcessing ? 0.6 : 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      transition: 'all 0.15s ease'
    });

    return (
      <div style={{
        display: 'flex',
        gap: '8px',
        marginTop: '16px',
        flexWrap: compact ? 'wrap' : 'nowrap'
      }}>
        {/* Complete - Primary action */}
        <button
          onClick={() => void handleComplete()}
          disabled={isProcessing}
          style={buttonStyle(true)}
          onMouseEnter={(e) => {
            if (!isProcessing) e.currentTarget.style.backgroundColor = '#059669';
          }}
          onMouseLeave={(e) => {
            if (!isProcessing) e.currentTarget.style.backgroundColor = '#10b981';
          }}
        >
          {isProcessing ? <Spin size="small" /> : <CheckCircleOutlined />}
          Complete
        </button>

        {/* Block - Exception path (UX Principle #7) */}
        {signal.available_actions.includes('blocked') && (
          <button
            onClick={() => setActionMode('block')}
            disabled={isProcessing}
            style={{
              ...buttonStyle(false),
              borderColor: '#fecaca',
              color: '#dc2626'
            }}
            onMouseEnter={(e) => {
              if (!isProcessing) e.currentTarget.style.backgroundColor = '#fef2f2';
            }}
            onMouseLeave={(e) => {
              if (!isProcessing) e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            <StopOutlined />
            Block
          </button>
        )}

        {/* Clarify */}
        {signal.available_actions.includes('clarification_requested') && (
          <button
            onClick={() => setActionMode('clarify')}
            disabled={isProcessing}
            style={{
              ...buttonStyle(false),
              borderColor: '#fcd34d',
              color: '#d97706'
            }}
            onMouseEnter={(e) => {
              if (!isProcessing) e.currentTarget.style.backgroundColor = '#fffbeb';
            }}
            onMouseLeave={(e) => {
              if (!isProcessing) e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            <QuestionCircleOutlined />
            Clarify
          </button>
        )}

        {/* Escalate */}
        {signal.available_actions.includes('escalated') && (
          <button
            onClick={() => setActionMode('escalate')}
            disabled={isProcessing}
            style={{
              ...buttonStyle(false),
              borderColor: '#c4b5fd',
              color: '#7c3aed'
            }}
            onMouseEnter={(e) => {
              if (!isProcessing) e.currentTarget.style.backgroundColor = '#f5f3ff';
            }}
            onMouseLeave={(e) => {
              if (!isProcessing) e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            <ArrowUpOutlined />
            Escalate
          </button>
        )}
      </div>
    );
  };

  // Render block form
  const renderBlockForm = () => {
    if (actionMode !== 'block') return null;

    return (
      <div style={{
        marginTop: '16px',
        padding: '16px',
        backgroundColor: '#fef2f2',
        borderRadius: '8px',
        border: '1px solid #fecaca'
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: 600,
          color: '#dc2626',
          marginBottom: '12px'
        }}>
          Why can't this be completed?
        </div>
        
        <BlockReasonSelector
          value={blockReason}
          onChange={setBlockReason}
        />

        {blockReason && (
          <div style={{ marginTop: '12px' }}>
            <TextArea
              placeholder="Additional notes (optional)"
              value={blockNotes}
              onChange={(e) => setBlockNotes(e.target.value)}
              rows={2}
              style={{ fontSize: '13px' }}
            />
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '12px'
        }}>
          <button
            onClick={() => {
              setActionMode('none');
              setBlockReason(null);
              setBlockNotes('');
            }}
            style={{
              flex: 1,
              padding: '8px 12px',
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              color: '#374151',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => void handleBlockSubmit()}
            disabled={!blockReason || isProcessing}
            style={{
              flex: 1,
              padding: '8px 12px',
              backgroundColor: blockReason ? '#dc2626' : '#e5e7eb',
              border: 'none',
              borderRadius: '6px',
              color: 'white',
              fontSize: '13px',
              fontWeight: 600,
              cursor: blockReason && !isProcessing ? 'pointer' : 'not-allowed',
              opacity: !blockReason || isProcessing ? 0.6 : 1
            }}
          >
            {isProcessing ? <Spin size="small" /> : 'Confirm Block'}
          </button>
        </div>
      </div>
    );
  };

  // Render escalate form
  const renderEscalateForm = () => {
    if (actionMode !== 'escalate') return null;

    return (
      <div style={{
        marginTop: '16px',
        padding: '16px',
        backgroundColor: '#f5f3ff',
        borderRadius: '8px',
        border: '1px solid #c4b5fd'
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: 600,
          color: '#7c3aed',
          marginBottom: '12px'
        }}>
          Why does this need escalation?
        </div>
        
        <TextArea
          placeholder="Reason for escalation"
          value={escalationReason}
          onChange={(e) => setEscalationReason(e.target.value)}
          rows={2}
          style={{ fontSize: '13px' }}
        />

        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '12px'
        }}>
          <button
            onClick={() => {
              setActionMode('none');
              setEscalationReason('');
            }}
            style={{
              flex: 1,
              padding: '8px 12px',
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              color: '#374151',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => void handleEscalateSubmit()}
            disabled={!escalationReason.trim() || isProcessing}
            style={{
              flex: 1,
              padding: '8px 12px',
              backgroundColor: escalationReason.trim() ? '#7c3aed' : '#e5e7eb',
              border: 'none',
              borderRadius: '6px',
              color: 'white',
              fontSize: '13px',
              fontWeight: 600,
              cursor: escalationReason.trim() && !isProcessing ? 'pointer' : 'not-allowed',
              opacity: !escalationReason.trim() || isProcessing ? 0.6 : 1
            }}
          >
            {isProcessing ? <Spin size="small" /> : 'Escalate'}
          </button>
        </div>
      </div>
    );
  };

  // Render clarify form
  const renderClarifyForm = () => {
    if (actionMode !== 'clarify') return null;

    return (
      <div style={{
        marginTop: '16px',
        padding: '16px',
        backgroundColor: '#fffbeb',
        borderRadius: '8px',
        border: '1px solid #fcd34d'
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: 600,
          color: '#d97706',
          marginBottom: '12px'
        }}>
          What do you need clarified?
        </div>
        
        <TextArea
          placeholder="Your question"
          value={clarificationQuestion}
          onChange={(e) => setClarificationQuestion(e.target.value)}
          rows={2}
          style={{ fontSize: '13px' }}
        />

        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '12px'
        }}>
          <button
            onClick={() => {
              setActionMode('none');
              setClarificationQuestion('');
            }}
            style={{
              flex: 1,
              padding: '8px 12px',
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              color: '#374151',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => void handleClarifySubmit()}
            disabled={!clarificationQuestion.trim() || isProcessing}
            style={{
              flex: 1,
              padding: '8px 12px',
              backgroundColor: clarificationQuestion.trim() ? '#d97706' : '#e5e7eb',
              border: 'none',
              borderRadius: '6px',
              color: 'white',
              fontSize: '13px',
              fontWeight: 600,
              cursor: clarificationQuestion.trim() && !isProcessing ? 'pointer' : 'not-allowed',
              opacity: !clarificationQuestion.trim() || isProcessing ? 0.6 : 1
            }}
          >
            {isProcessing ? <Spin size="small" /> : 'Ask'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      padding: compact ? '16px' : '20px',
      backgroundColor: 'white',
      borderRadius: '12px',
      border: `2px solid ${priorityColors.border}`,
      boxShadow: signal.priority <= 2 ? '0 4px 12px rgba(220, 38, 38, 0.1)' : 'none'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        marginBottom: '12px'
      }}>
        {/* Priority indicator */}
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '8px',
          backgroundColor: priorityColors.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          {getSignalTypeIcon()}
        </div>

        {/* Signal info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '4px'
          }}>
            <span style={{
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: priorityColors.text,
              backgroundColor: priorityColors.bg,
              padding: '2px 6px',
              borderRadius: '4px'
            }}>
              {signal.priority <= 2 ? 'High Priority' : signal.priority <= 4 ? 'Normal' : 'Low'}
            </span>
            <span style={{
              fontSize: '11px',
              color: '#9ca3af'
            }}>
              {new Date(signal.created_at).toLocaleTimeString()}
            </span>
          </div>
        </div>
      </div>

      {/* Message */}
      <div style={{
        fontSize: compact ? '15px' : '16px',
        fontWeight: 500,
        color: '#111827',
        lineHeight: 1.5
      }}>
        {signal.message}
      </div>

      {/* Context (pull-based) */}
      {renderContext()}

      {/* Actions */}
      {renderActions()}
      {renderBlockForm()}
      {renderEscalateForm()}
      {renderClarifyForm()}
    </div>
  );
};
